import { utc } from '@date-fns/utc'
import { keepPreviousData } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ArrowRightIcon, ChevronDownIcon, LockIcon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { useEmbeddedPartner } from 'components/hooks/employer_onboarding/useEmbeddedPartner'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Banner, Button, Pagination, Table, TextButton, TextLink } from 'slab'
import { calculateAgeAtDate } from 'utils'
import {
  useGetQuotingProspectsProspectIdCensusCensusIdEmployees,
  usePostQuotingProspectsProspectIdCensusCensusIdEmployeesBulkReplaceDependents,
} from 'utils/web_api'
import { type QuotingEmployee } from 'utils/web_api.schemas'

import { useCensus } from '../CensusProvider'
import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent, parseError } from '../utils'
import { DependentBirthdaysContent } from './census/DependentBirthdaysPopover'
import { DependentCounter } from './census/DependentCounter'

type Dependent = { relationship: 'spouse' | 'child' | 'self'; date_of_birth: string }

const PAGE_SIZE = 25

function formatDOB (dateOfBirth: string) {
  const date = new Date(dateOfBirth)
  return format(date, 'M/d/yy', { in: utc })
}

interface EmployeeRowProps {
  employee: QuotingEmployee
  dependents: Dependent[]
  onIncrement: () => void
  onDecrement: () => void
  onDependentChange: (index: number, dateOfBirth: string) => void
  disabled: boolean
}

function EmployeeRow ({
  employee,
  dependents,
  onIncrement,
  onDecrement,
  onDependentChange,
  disabled,
}: EmployeeRowProps) {
  return (
    <Table.Row>
      <Table.RowHeader sticky>
        <span className='sensitive truncate block' title={employee.name ?? undefined}>
          {employee.name}
        </span>
      </Table.RowHeader>
      <Table.Cell>
        <span className='sensitive'>{employee.zip || '—'}</span>
      </Table.Cell>
      <Table.Cell>
        <span className='sensitive'>{formatDOB(employee.date_of_birth)}</span>
      </Table.Cell>
      <Table.Cell>
        <DependentCounter
          count={dependents.length}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          disabled={disabled}
          popoverContent={
            dependents.length > 0
              ? (
                <DependentBirthdaysContent
                  dependents={dependents}
                  onDependentChange={onDependentChange}
                />
                )
              : undefined
          }
        />
      </Table.Cell>
      <Table.Cell align='right'>
        <span className='sensitive'>
          {calculateAgeAtDate(employee.date_of_birth, new Date())}
        </span>
      </Table.Cell>
    </Table.Row>
  )
}

export function EmbeddedCensusDependentsPage () {
  useEmbeddedPartner() // Keep hook call for side effects
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { employer } = useEmployer()
  const { census } = useCensus()
  const { setError, errorBanner } = useAPIErrorBanner({ fallbackMessage: 'Failed to save dependents' })

  const prospectId = employer?.quoting_prospect_id ?? ''
  const censusId = census?.id ?? ''

  const [pageIndex, setPageIndex] = useState(0)
  const [showIneligible, setShowIneligible] = useState(false)
  const [localDependents, setLocalDependents] = useState<Record<string, Dependent[]>>({})

  const { data: employeesResp, isLoading, error } = useGetQuotingProspectsProspectIdCensusCensusIdEmployees(
    prospectId,
    censusId,
    {
      'page[size]': PAGE_SIZE,
      'page[number]': pageIndex + 1,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        refetchOnMount: 'always',
        enabled: !!censusId,
      },
    }
  )

  const allEmployees = employeesResp?.data ?? []
  const totalEmployees = employeesResp?.pagination?.total_records ?? 0

  // Split employees into eligible and ineligible
  const { eligibleEmployees, ineligibleEmployees } = useMemo(() => {
    const eligible: QuotingEmployee[] = []
    const ineligible: QuotingEmployee[] = []

    allEmployees.forEach((emp) => {
      if (emp.eligible) {
        eligible.push(emp)
      } else {
        ineligible.push(emp)
      }
    })

    return { eligibleEmployees: eligible, ineligibleEmployees: ineligible }
  }, [allEmployees])

  // Get dependents for an employee (local state takes precedence)
  const getDependents = useCallback((employeeId: string, serverDependents: Dependent[]): Dependent[] => {
    return localDependents[employeeId] ?? serverDependents
  }, [localDependents])

  // Update local dependents for an employee
  const updateLocalDependents = useCallback((employeeId: string, dependents: Dependent[]) => {
    setLocalDependents((prev) => ({
      ...prev,
      [employeeId]: dependents,
    }))
  }, [])

  // Handle increment/decrement of dependents
  const handleIncrementDependents = useCallback((employee: QuotingEmployee) => {
    const currentDependents = getDependents(employee.id, employee.dependents as Dependent[])
    const newDependents = [
      ...currentDependents,
      { relationship: 'spouse' as const, date_of_birth: '' },
    ]
    updateLocalDependents(employee.id, newDependents)
  }, [getDependents, updateLocalDependents])

  const handleDecrementDependents = useCallback((employee: QuotingEmployee) => {
    const currentDependents = getDependents(employee.id, employee.dependents as Dependent[])
    if (currentDependents.length > 0) {
      const newDependents = currentDependents.slice(0, -1)
      updateLocalDependents(employee.id, newDependents)
    }
  }, [getDependents, updateLocalDependents])

  // Handle dependent birthday change
  const handleDependentChange = useCallback((employeeId: string, dependentIndex: number, dateOfBirth: string) => {
    const employee = allEmployees.find((e) => e.id === employeeId)
    if (!employee) return

    const currentDependents = getDependents(employeeId, employee.dependents as Dependent[])
    const newDependents = currentDependents.map((dep, idx) =>
      idx === dependentIndex ? { ...dep, date_of_birth: dateOfBirth } : dep
    )
    updateLocalDependents(employeeId, newDependents)
  }, [allEmployees, getDependents, updateLocalDependents])

  const dependentsMutation = usePostQuotingProspectsProspectIdCensusCensusIdEmployeesBulkReplaceDependents({
    mutation: {
      onSuccess: () => {
        emitEmployerOnboardingEvent('[ER] Dependents updated', {})
        navigateToNextStep()
      },
      onError: setError,
    },
  })

  const handleSubmit = useCallback(() => {
    // Calculate a default birthdate for a 12-year-old (for children without birthdates)
    const today = new Date()
    const twelveYearsAgo = new Date(today.getFullYear() - 12, today.getMonth(), today.getDate())
    const defaultChildBirthdate = twelveYearsAgo.toISOString().split('T')[0]

    // Build updates from local state merged with server data
    // Fill in default birthdates for dependents without them
    const updatesByEmployeeId = allEmployees.reduce<Record<string, Dependent[]>>((acc, employee) => {
      const dependents = getDependents(employee.id, employee.dependents as Dependent[])

      // Fill in missing birthdates with defaults
      const dependentsWithDates = dependents.map((dep, index) => {
        if (dep.date_of_birth) {
          return dep
        }

        // First dependent without birthdate: use employee's age
        // Other dependents without birthdate: assume 12 years old
        const defaultDate = index === 0 ? employee.date_of_birth : defaultChildBirthdate

        return { ...dep, date_of_birth: defaultDate }
      })

      acc[employee.id] = dependentsWithDates
      return acc
    }, {})

    dependentsMutation.mutate({
      prospectId,
      censusId,
      data: { updates_by_employee_id: updatesByEmployeeId },
    })
  }, [allEmployees, getDependents, dependentsMutation, prospectId, censusId])

  function renderContent () {
    if (!prospectId || !censusId) {
      return (
        <Banner type='warning' title='Cannot edit dependents for this employer'>
          We cannot edit dependents for the current employer. If this problem persists, please contact support.
        </Banner>
      )
    }

    if (isLoading) {
      return (
        <div className='flex items-center justify-center p-10'>
          <div className='text-gray-500'>Loading...</div>
        </div>
      )
    }

    if (error) {
      return (
        <Banner type='critical' title='An error occurred loading your account information'>
          {parseError(error, '').message}
        </Banner>
      )
    }

    return (
      <div className='bg-white rounded-2xl border border-gray-200 shadow overflow-hidden'>
        <Table>
          <colgroup>
            <col style={{ width: '35%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader sticky>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Zip code</Table.ColumnHeader>
              <Table.ColumnHeader>Date of birth</Table.ColumnHeader>
              <Table.ColumnHeader>Dependents</Table.ColumnHeader>
              <Table.ColumnHeader align='right'>Age</Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {eligibleEmployees.map((employee) => (
              <EmployeeRow
                key={employee.id}
                employee={employee}
                dependents={getDependents(employee.id, employee.dependents as Dependent[])}
                onIncrement={() => handleIncrementDependents(employee)}
                onDecrement={() => handleDecrementDependents(employee)}
                onDependentChange={(index, dateOfBirth) => {
                  handleDependentChange(employee.id, index, dateOfBirth)
                }}
                disabled={dependentsMutation.isPending}
              />
            ))}
            {showIneligible && ineligibleEmployees.map((employee) => (
              <EmployeeRow
                key={employee.id}
                employee={employee}
                dependents={getDependents(employee.id, employee.dependents as Dependent[])}
                onIncrement={() => handleIncrementDependents(employee)}
                onDecrement={() => handleDecrementDependents(employee)}
                onDependentChange={(index, dateOfBirth) => {
                  handleDependentChange(employee.id, index, dateOfBirth)
                }}
                disabled={dependentsMutation.isPending}
              />
            ))}
          </Table.Body>
        </Table>

        <div className='flex items-center justify-between px-4 py-4 border-t border-gray-200'>
          {ineligibleEmployees.length > 0
            ? (
              <TextButton
                color='gray'
                onClick={() => setShowIneligible(!showIneligible)}
                rightIcon={ChevronDownIcon}
              >
                {showIneligible ? 'Hide' : 'Show'} {ineligibleEmployees.length} ineligible employee{ineligibleEmployees.length === 1 ? '' : 's'}
              </TextButton>
              )
            : <div />}
          <Pagination
            pageIndex={pageIndex}
            perPage={PAGE_SIZE}
            totalRows={totalEmployees}
            onPageChange={setPageIndex}
          />
        </div>
      </div>
    )
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading="Here's what we have"
        subheading='Take a look and confirm everything looks right. You can select which employees are eligible for benefits for a more accurate quote.'
        mb={10}
      />
      <CenteredLayout.Container padded={false} hideCard>
        {renderContent()}
        {errorBanner}
      </CenteredLayout.Container>

      <div className='mt-6'>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            rightIcon={ArrowRightIcon}
            disabled={dependentsMutation.isPending}
          >
            Looks good to me
          </Button>
        </CenteredLayout.Buttons>
      </div>

      <div className='flex items-start gap-2 justify-center mt-6 text-gray-500 max-w-[720px] mx-auto'>
        <LockIcon size={16} className='mt-0.5 flex-shrink-0' />
        <p className='body text-gray-500'>
          Thatch does not sell your data in any way, shape, or form. Our SOC 2 Type II certification means the data you share with Thatch is protected and secure. Learn more at our{' '}
          <TextLink href='https://trust.thatch.com/' target='_blank' rel='noopener noreferrer'>
            Trust Center
          </TextLink>
          .
        </p>
      </div>
    </CenteredLayout>
  )
}
